import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: rpCtrlSalesSA (master) + rpCtrlSalesSA_sub (detail). BE renders
// the master-detail HTML (header band + repeated DO-line rows + footer total /
// signature); this dialog is PRESENTATIONAL — `html` arrives via props (the
// parent fetches via RTK lazy query). Mirror DeliveryPrintDialog: iframe srcDoc
// + A4 PRINT_PREVIEW_CSS override + Print → printAndUploadReport.

interface SaPrintDialogProps {
  open: boolean
  title: string
  html: string
  loading: boolean
  canEdit?: boolean
  onClose: () => void
}

export default function SaPrintDialog({
  open,
  title,
  html,
  loading,
  canEdit = true,
  onClose,
}: SaPrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const iframeTitle = 'Shipping Advice Print Preview'
  // Shipping Advice = report có bảng chi tiết co giãn → BE trả nhiều tờ `.sa-page`
  // (bảng nhiều dòng tự break trang). `pageSelector` cho mỗi .sa-page thành 1 tờ A4
  // riêng trên nền xám (preview tách trang = PDF).
  const previewHtml = html
    ? buildPrintPreviewHtml(html, {
        styleId: 'sa-print-preview-overrides',
        pageSelector: '.sa-page',
        pagePadding: '9mm 6mm',
        extraCss: '.sa-page + .sa-page { break-before: page; page-break-before: always; }',
      })
    : ''

  return (
    <>
      {open && loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(previewHtml)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            {title}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  // "Shipping Advice (S-03142 - SGN3094200)" → "S-03142_ShippingAdvice.pdf"
                  // (lấy mã đầu trước " - " trong ngoặc, đưa lên đầu).
                  const idInParens = title.replace(/^.*\(([^)]+)\).*$/, '$1')
                  const primaryId = idInParens.split(/\s+-\s+/)[0].trim()
                  const safeId = primaryId.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_') || 'shipping_advice'
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesSA',
                    printTitle: `${safeId}_ShippingAdvice.pdf`,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
